import { Injectable, Inject, forwardRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Amplify, Auth } from 'aws-amplify';
import awsConfig from '../config/aws-exports';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ApplicationConfigService } from '../application-config.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

export interface IUser {
  email: string;
  password: string;
  showPassword?: boolean;
  code?: string;
  name?: string;
}

export interface TokenRefreshConfig {
  bufferTimeMinutes: number;
}
@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private readonly authenticationSubject: BehaviorSubject<boolean>;
  resourceUrl: string;
  private readonly loginUrl = environment.serverless.baseUrl;
  private readonly authorization = environment.serverless.authorization;

  // Token cache
  private cachedAccessToken: string | null = null;
  private tokenExpiryTime: number = 0;
  private actualTokenExpiryTime: number = 0;

  // User info cache
  private cachedUserInfo: any = null;
  private userInfoExpiryTime: number = 0;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // Configuration
  private readonly config: TokenRefreshConfig = {
    bufferTimeMinutes: 2 // Refresh token 2 minutes before expiry
  };

  constructor(
    protected http: HttpClient,
    private readonly applicationConfigService: ApplicationConfigService,
    private readonly router: Router
  ) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor();
    Amplify.configure(awsConfig);
    this.authenticationSubject = new BehaviorSubject<boolean>(false);
    Auth.currentAuthenticatedUser()
      .then(() => this.authenticationSubject.next(true))
      .catch(() => this.authenticationSubject.next(false));
  }
  public get isAuthenticated$() {
    return this.authenticationSubject.asObservable();
  }

  /**
 * Parse JWT token and extract expiration time
 */
  private parseJwtToken(token: string): { exp: number } | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  }

  /**
   * Check if token needs refresh based on expiry time and buffer
   */
  private isTokenNearExpiry(): boolean {
    if (!this.actualTokenExpiryTime) {
      return true; // If we don't have expiry time, assume it needs refresh
    }
    const now = Date.now();
    const bufferTime = this.config.bufferTimeMinutes * 60 * 1000;
    return now >= (this.actualTokenExpiryTime - bufferTime);
  }

  /**
   * Check if token is actually expired
   */
  private isTokenExpired(): boolean {
    if (!this.actualTokenExpiryTime) {
      return true;
    }
    return Date.now() >= this.actualTokenExpiryTime;
  }

  /**
   * Get observable for token refresh status
   */
  public get refreshToken$(): Observable<string | null> {
    return this.refreshTokenSubject.asObservable();
  }

  /**
   * Check if token refresh is in progress
   */
  public get isTokenRefreshing(): boolean {
    return this.isRefreshing;
  }

  /**
   * Refresh the access token
   */
  public async refreshAccessToken(): Promise<string> {
    if (this.isRefreshing) {
      // If refresh is already in progress, wait for it to complete
      return new Promise((resolve, reject) => {
        this.refreshToken$.subscribe({
          next: (token) => {
            if (token) {
              resolve(token);
            }
          },
          error: (err) => reject(err)
        });
      });
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    try {
      // Force Cognito to refresh the session using the refresh token
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();

      // Parse token to get actual expiration time
      const tokenPayload = this.parseJwtToken(accessToken);
      if (tokenPayload && tokenPayload.exp) {
        this.actualTokenExpiryTime = tokenPayload.exp * 1000; // Convert to milliseconds
      }

      // Update cache
      this.cachedAccessToken = accessToken;
      const now = Date.now();
      this.tokenExpiryTime = now + (55 * 60 * 1000); // Cache for 55 minutes

      this.isRefreshing = false;
      this.refreshTokenSubject.next(accessToken);

      return accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.isRefreshing = false;
      this.refreshTokenSubject.next(null);

      // Clear all caches
      this.clearTokenCache();

      // Handle refresh failure - logout and redirect
      await this.handleRefreshFailure();

      throw error;
    }
  }

  /**
   * Clear token cache
   */
  private clearTokenCache(): void {
    this.cachedAccessToken = null;
    this.tokenExpiryTime = 0;
    this.actualTokenExpiryTime = 0;
  }

  /**
   * Handle token refresh failure
   */
  private async handleRefreshFailure(): Promise<void> {
    try {
      this.authenticationSubject.next(false);
      this.clearTokenCache();
      this.cachedUserInfo = null;
      this.userInfoExpiryTime = 0;

      // Clear session storage
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('lastKnownUserRole');
      }

      // Sign out from Cognito
      await Auth.signOut();

      // Redirect to login
      this.router.navigate(['/auth/login']);
    } catch (error) {
      console.error('Error during logout after refresh failure:', error);
      // Force redirect even if signOut fails
      this.router.navigate(['/auth/login']);
    }
  }
  public signIn(user: IUser): Promise<any> {
    return Auth.signIn(user.email, user.password)
      .then(async (response: any) => {
        // Handle "NEW_PASSWORD_REQUIRED" challenge
        if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // The user is required to set a new password
          try {
            await Auth.completeNewPassword(response, user.password);
            this.authenticationSubject.next(true);
            // Preload token and user info into cache
            await this.getAccessToken();
            await this.getCachedUserInfo();
            return Promise.resolve('Password updated successfully.');
          } catch (error) {
            console.error('Error completing new password challenge:', error);
            return Promise.reject(error);
          }
        } else if (response.challengeName === 'SMS_MFA') {
          // SMS MFA is required; notify the UI
          return Promise.resolve({ challengeName: 'SMS_MFA', response });
        } else {
          this.authenticationSubject.next(true);
          // Preload token and user info into cache
          await this.getAccessToken();
          await this.getCachedUserInfo();
          return Promise.resolve('Sign-in successful.');
        }
      })
      .catch((error: any) => {
        console.error('Sign-in error:', error);
        return Promise.reject(error);
      });
  }
  public confirmMFA(user: IUser, code: string): Promise<string> {
    return Auth.confirmSignIn(user, code, 'SMS_MFA') // 'SMS_MFA' is explicitly passed
      .then(async () => {
        this.authenticationSubject.next(true);
        // Preload token and user info into cache
        await this.getCachedUserInfo();
        return this.getAccessToken(); // Fetch and return access token
      })
      .catch((error: any) => {
        console.error('Error confirming MFA:', error);
        return Promise.reject(error);
      });
  }

  public async getAccessToken(): Promise<string> {
    const now = Date.now();
    // Token cache duration: 55 minutes (tokens typically expire in 60 minutes)
    const TOKEN_CACHE_DURATION = 55 * 60 * 1000;

    // Return cached token if it's still valid
    if (this.cachedAccessToken && now < this.tokenExpiryTime && !this.isTokenNearExpiry()) {
      return Promise.resolve(this.cachedAccessToken);
    }

    if (this.cachedAccessToken && this.isTokenNearExpiry()) {
      try {
        return await this.refreshAccessToken();
      } catch (error) {
        console.error('Error refreshing token:', error);
        throw error;
      }
    }
    // Fetch new token from Cognito (first time or after error)
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();

      // Parse token to get actual expiration time
      const tokenPayload = this.parseJwtToken(accessToken);
      if (tokenPayload && tokenPayload.exp) {
        this.actualTokenExpiryTime = tokenPayload.exp * 1000; // Convert to milliseconds
      }

      // Cache the token
      this.cachedAccessToken = accessToken;
      this.tokenExpiryTime = now + TOKEN_CACHE_DURATION;

      return accessToken;
    } catch (error: any) {
      console.error('Error fetching access token:', error);
      // Clear cache on error
      this.clearTokenCache();
      throw error;
    }
  }
  public signOut(): Promise<any> {
    return Auth.signOut()
      .then(() => {
        this.authenticationSubject.next(false);
        // Clear token cache on logout
        this.clearTokenCache();
        // Clear user info cache on logout
        this.cachedUserInfo = null;
        this.userInfoExpiryTime = 0;
        // Clear role cache on logout
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('lastKnownUserRole');
        }
      })
      .catch((error: any) => {
        console.error('Sign-out error:', error);
        throw error;
      });
  }
  public isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return Promise.resolve(true);
    } else {
      return Auth.currentSession()
        .then(() => true)
        .catch(() => false);
    }
  }

  public changePassword(oldPassword: string, newPassword: string): Promise<any> {
    if (oldPassword === newPassword) {
      return Promise.reject('Old password and new password cannot be same.');
    }
    return Auth.currentAuthenticatedUser()
      .then((user) => {
        return Auth.changePassword(user, oldPassword, newPassword)
          .then(() => {
            return 'Password changed successfully.';
          })
          .catch((error) => {
            console.error('Error changing password:', error);
            return Promise.reject(error);
          });
      })
      .catch((error) => {
        console.error('Error fetching authenticated user:', error);
        return Promise.reject(error);
      });
  }
  private async getCachedUserInfo(): Promise<any> {
    const now = Date.now();
    // User info cache duration: 55 minutes
    const USER_INFO_CACHE_DURATION = 55 * 60 * 1000;

    // Return cached user info if it's still valid
    if (this.cachedUserInfo && now < this.userInfoExpiryTime) {
      return Promise.resolve(this.cachedUserInfo);
    }

    // Fetch new user info from Cognito
    try {
      const userInfo = await Auth.currentUserInfo();
      // Cache the user info
      this.cachedUserInfo = userInfo;
      this.userInfoExpiryTime = now + USER_INFO_CACHE_DURATION;
      return userInfo;
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Clear cache on error
      this.cachedUserInfo = null;
      this.userInfoExpiryTime = 0;
      return Promise.reject(error);
    }
  }

  public getUser(): Promise<any> {
    return this.getCachedUserInfo();
  }

  public isInCSRole(): Promise<boolean> {
    return this.getCachedUserInfo()
      .then((res: any) => {
        const roles: string = res.attributes['custom:role'];
        return roles.includes('customer_support');
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public getCurrentUserRoles(): Promise<string> {
    return this.getCachedUserInfo()
      .then((res: any) => {
        const roles: string = res.attributes['custom:role'];
        return roles;
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public updateUser(user: IUser): Promise<any> {
    return Auth.currentUserPoolUser().then((cognitoUser: any) => {
      const attributes = { name: user.name, email: user.email };
      return Auth.updateUserAttributes(cognitoUser, attributes);
    });
  }

  public forgotPassword(username: string): Promise<any> {
    return Auth.forgotPassword(username, { DeliveryMedium: 'EMAIL' })
      .then((res: any) => {
        return 'Code sent to your email.';
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public confirmForgotPassword(username: string, code: string, newPassword: string): Promise<any> {
    return Auth.forgotPasswordSubmit(username, code, newPassword)
      .then((res: any) => {
        return 'Password successfully reset!.';
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public submitPassword(payload: { userId: string; password: string }): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/admin-users/set-forget-password`, payload);
  }



  sendOtp(phoneNo: any): Observable<any> {
    const params = new HttpParams().set('phoneNo', phoneNo);

    return this.http.get<any>(`${this.resourceUrl}/admin-users/forget-password`, { params });
  }

  sendLoginOtp(destination: string) {
    const payload = {
      otp_type: "sms",
      destination: '+880' + destination
    };

    const headers = new HttpHeaders({
      Authorization: this.authorization
    });

    const options = { headers };

    return this.http.post<any>(`${this.loginUrl}/admin/login-2fa-send-otp`, payload, options);
  }

  verifyLoginOTP(otp: string, destination: string) {
    let payload = {
      otp_type: "sms",
      destination: '+880' + destination,
      otp: otp
    };

    const headers = {
      headers: new HttpHeaders()
        .set('Authorization', `${this.authorization}`)
    }

    return this.http.post<any>(`${this.loginUrl}/admin/login-2fa-verify-otp`, payload, headers);
  }
}
